import { isEmpty, ofArray, filter, pairwise, cons, tail, mapIndexed, max as max_1, singleton, item, empty, collect, head, map, length, sortByDescending } from "../fable_modules/fable-library-js.4.28.0/List.js";
import { List_groupBy, List_distinctBy } from "../fable_modules/fable-library-js.4.28.0/Seq2.js";
import { createObj, equals, comparePrimitives, stringHash } from "../fable_modules/fable-library-js.4.28.0/Util.js";
import { TagFormat_parse } from "../Shared/Types.js";
import { min, max } from "../fable_modules/fable-library-js.4.28.0/Double.js";
import { item as item_1 } from "../fable_modules/fable-library-js.4.28.0/Array.js";
import { createElement } from "react";
import { Msg } from "../App/Messages.js";
import { empty as empty_1, collect as collect_1, singleton as singleton_1, append, delay, toList } from "../fable_modules/fable-library-js.4.28.0/Seq.js";
import { reactApi } from "../fable_modules/Feliz.2.9.0/Interop.fs.js";
import { t } from "../Lang.js";
import { Tags_noTagsMessage } from "../Shared/Tr.js";

const canvasWidth = 900;

const canvasHeight = 600;

const tagColors = [["#e8c97a", "#b8872e"], ["#f2d1c4", "#c06a45"], ["#d0dfc6", "#6b8e5a"], ["#c4daf2", "#4572a7"], ["#f2e4c4", "#a07030"], ["#d4c4f2", "#6b45a7"], ["#f2c4c4", "#a74545"], ["#c4f2e4", "#2e8b6e"], ["#f2d9c4", "#a76030"], ["#c4d4f2", "#4560a7"]];

function collectTagClusters(guests) {
    return sortByDescending((tupledArg_2) => length(tupledArg_2[1]), map((tupledArg_1) => {
        const pairs = tupledArg_1[1];
        return [head(pairs)[0], List_distinctBy((g_1) => g_1.Id, map((tuple_1) => tuple_1[1], pairs), {
            Equals: (x_1, y_1) => (x_1 === y_1),
            GetHashCode: stringHash,
        })];
    }, List_groupBy((tupledArg) => tupledArg[0].toLowerCase(), collect((g) => map((tag) => [tag, g], TagFormat_parse(g.Tags)), guests), {
        Equals: (x, y) => (x === y),
        GetHashCode: stringHash,
    })), {
        Compare: comparePrimitives,
    });
}

function clusterRadius(guestCount) {
    return max(40, Math.sqrt(guestCount) * 22);
}

function layoutClusters(clusters) {
    const n = length(clusters) | 0;
    switch (n) {
        case 0:
            return empty();
        case 1: {
            const patternInput = item(0, clusters);
            const guests = patternInput[1];
            return singleton([patternInput[0], guests, canvasWidth / 2, canvasHeight / 2, clusterRadius(length(guests)), 0]);
        }
        default: {
            const maxR = max_1(map((tupledArg) => clusterRadius(length(tupledArg[1])), clusters), {
                Compare: comparePrimitives,
            });
            let arrangementRadius;
            const baseR = (n * maxR) / 3.141592653589793;
            arrangementRadius = max(maxR + 40, min(min(((canvasWidth / 2) - maxR) - 20, ((canvasHeight / 2) - maxR) - 20), baseR));
            const cx = canvasWidth / 2;
            const cy = canvasHeight / 2;
            return mapIndexed((i, tupledArg_1) => {
                const guests_1 = tupledArg_1[1];
                const angle = (((2 * 3.141592653589793) * i) / n) - (3.141592653589793 / 2);
                return [tupledArg_1[0], guests_1, cx + (arrangementRadius * Math.cos(angle)), cy + (arrangementRadius * Math.sin(angle)), clusterRadius(length(guests_1)), i % tagColors.length];
            }, clusters);
        }
    }
}

function layoutGuestDots(guests, cx, cy, clusterR) {
    const n = length(guests) | 0;
    switch (n) {
        case 0:
            return empty();
        case 1:
            return singleton([item(0, guests), cx, cy]);
        default:
            if (n <= 6) {
                const ringR = clusterR * 0.55;
                return mapIndexed((i, g) => {
                    const angle = (((2 * 3.141592653589793) * i) / n) - (3.141592653589793 / 2);
                    return [g, cx + (ringR * Math.cos(angle)), cy + (ringR * Math.sin(angle))];
                }, guests);
            }
            else {
                const ringGuests = tail(guests);
                const ringR_1 = clusterR * 0.6;
                const ringN = length(ringGuests) | 0;
                const dots = mapIndexed((i_1, g_1) => {
                    const angle_1 = (((2 * 3.141592653589793) * i_1) / ringN) - (3.141592653589793 / 2);
                    return [g_1, cx + (ringR_1 * Math.cos(angle_1)), cy + (ringR_1 * Math.sin(angle_1))];
                }, ringGuests);
                return cons([item(0, guests), cx, cy], dots);
            }
    }
}

function findMultiTagConnections(clusterLayout) {
    return collect((tupledArg_3) => map((tupledArg_4) => {
        const _arg_1 = tupledArg_4[0];
        const _arg_2 = tupledArg_4[1];
        return [_arg_1[0], _arg_1[1], _arg_1[2], _arg_2[1], _arg_2[2]];
    }, pairwise(tupledArg_3[1])), filter((tupledArg_2) => (length(tupledArg_2[1]) > 1), map((tupledArg_1) => [tupledArg_1[0], map((tuple_1) => tuple_1[1], tupledArg_1[1])], List_groupBy((tuple) => tuple[0], collect((tupledArg) => map((g) => [g.Id, [g, tupledArg[2], tupledArg[3]]], tupledArg[1]), clusterLayout), {
        Equals: (x, y) => (x === y),
        GetHashCode: stringHash,
    }))));
}

function renderCluster(tag, guests, cx, cy, radius, colorIdx, hoveredTag, selectedTag, dispatch) {
    let elements_1;
    const patternInput = item_1(colorIdx % tagColors.length, tagColors);
    const strokeColor = patternInput[1];
    const isHovered = equals(hoveredTag, tag);
    const isSelected = equals(selectedTag, tag);
    const opacity = (isHovered ? true : isSelected) ? "0.9" : "0.5";
    const strokeWidth = (isHovered ? true : isSelected) ? "3" : "1.5";
    const guestDots = layoutGuestDots(guests, cx, cy, radius);
    return createElement("g", createObj(ofArray([["className", "tag-cluster-group"], ["onMouseEnter", (_arg) => {
        dispatch(new Msg(74, [tag]));
    }], ["onMouseLeave", (_arg_1) => {
        dispatch(new Msg(74, [""]));
    }], ["onClick", (_arg_2) => {
        dispatch(new Msg(74, [tag]));
    }], (elements_1 = toList(delay(() => append(singleton_1(createElement("circle", {
        cx: cx,
        cy: cy,
        r: radius,
        fill: patternInput[0],
        stroke: strokeColor,
        strokeWidth: strokeWidth,
        opacity: opacity,
        className: "tag-cluster-bg",
    })), delay(() => append(singleton_1(createElement("text", {
        x: cx,
        y: (cy - radius) - 8,
        textAnchor: "middle",
        dominantBaseline: "auto",
        className: "tag-cluster-label",
        children: `${tag} (${length(guests)})`,
    })), delay(() => append(collect_1((matchValue) => {
        let elements;
        const guest = matchValue[0];
        return singleton_1(createElement("g", createObj(ofArray([["className", "tag-guest-dot-group"], (elements = ofArray([createElement("circle", {
            cx: matchValue[1],
            cy: matchValue[2],
            r: 6,
            fill: strokeColor,
            opacity: "0.85",
            className: "tag-guest-dot",
        }), createElement("title", {
            children: `${guest.FirstName} ${guest.LastName}`,
        })]), ["children", reactApi.Children.toArray(Array.from(elements))])]))));
    }, guestDots), delay(() => (((length(guests) <= 8) && (radius >= 50)) ? collect_1((matchValue_1) => singleton_1(createElement("text", {
        x: matchValue_1[1],
        y: matchValue_1[2] + 14,
        textAnchor: "middle",
        dominantBaseline: "auto",
        pointerEvents: "none",
        className: "tag-guest-name-label",
        children: matchValue_1[0].FirstName,
    })), guestDots) : empty_1()))))))))), ["children", reactApi.Children.toArray(Array.from(elements_1))])])));
}

function renderConnections(connections) {
    let elements;
    return createElement("g", createObj(ofArray([["className", "tag-connections"], (elements = toList(delay(() => collect_1((matchValue) => singleton_1(createElement("line", {
        x1: matchValue[1],
        y1: matchValue[2],
        x2: matchValue[3],
        y2: matchValue[4],
        className: "tag-connection-line",
    })), connections))), ["children", reactApi.Children.toArray(Array.from(elements))])])));
}

/**
 * Main tag visualization view - SVG bubble chart.
 */
export function view(state, dispatch) {
    let elems, elems_2, elements;
    const clusters = collectTagClusters(state.Guests);
    const clusterLayout = layoutClusters(clusters);
    if (isEmpty(clusters)) {
        return createElement("div", createObj(ofArray([["className", "tag-viz-empty"], (elems = [createElement("p", {
            className: "text-muted",
            children: t(Tags_noTagsMessage),
        })], ["children", reactApi.Children.toArray(Array.from(elems))])])));
    }
    else {
        const connections = findMultiTagConnections(clusterLayout);
        const hoveredTag = (state.GuestSearch.trim() !== "") ? state.GuestSearch : undefined;
        return createElement("div", createObj(ofArray([["className", "tag-viz-container"], (elems_2 = [createElement("svg", createObj(ofArray([["className", "tag-viz-svg"], ["viewBox", (((((0 + " ") + 0) + " ") + ~~canvasWidth) + " ") + ~~canvasHeight], ["preserveAspectRatio", "xMidYMid meet"], (elements = toList(delay(() => append(singleton_1(createElement("rect", {
            x: 0,
            y: 0,
            width: canvasWidth,
            height: canvasHeight,
            className: "tag-viz-bg",
        })), delay(() => append(singleton_1(renderConnections(connections)), delay(() => collect_1((matchValue) => singleton_1(renderCluster(matchValue[0], matchValue[1], matchValue[2], matchValue[3], matchValue[4], matchValue[5], hoveredTag, hoveredTag, dispatch)), clusterLayout))))))), ["children", reactApi.Children.toArray(Array.from(elements))])])))], ["children", reactApi.Children.toArray(Array.from(elems_2))])])));
    }
}

